/**
 * The snackbar. SPEC.md §12.1, §12.2, §35, §4.4.
 *
 * "Entered -60 / UNDO", alive for the shared UNDO_TOAST_DURATION_MS, and the same
 * lane a closed hand's summary arrives in. The countdown lives in uiStore so a
 * toast still expires if whatever pushed it unmounts, and UNDO calls the server —
 * which reverses the entry with inverse transfers. Nothing is edited or deleted
 * locally, here or anywhere else. §12.1, §12.2
 *
 * It is the app's only toast host — mounted once in App, not per screen: an error
 * notice is the same object with different copy, and a player bounced back to the
 * landing page still has to be able to read why. §35
 *
 * It sits at the top, under the top bar, and that is a deliberate choice about
 * what a floating notice is allowed to cover. Not the keypad: a snackbar over the
 * keys turns the next tap into an accidental UNDO. Not the top bar itself: the
 * gear is what a player reaches for the moment something went wrong. And no
 * longer over the player boxes, which is where it used to land — those are the
 * only scores on the screen, and hiding them just as one of them changes is the
 * one thing this notice must not do. What it covers instead is the balance line,
 * which is a glance rather than a target. §10.1, §14.1, §18.4
 */

import type { CSSProperties } from 'react';
import { Toast } from '@/components/ui/Toast';
import { useToasts, useUiStore } from '@/stores/ui-store';

/** Older notices stay in the queue but off screen; the newest matters most. */
const VISIBLE_LIMIT = 3;

/**
 * --snackbar-top is published on the document root by GamePage as the measured
 * height of the top bar, so the notice lands flush beneath it. The fallback is
 * the safe-area inset, for the landing and join screens: they have no top bar,
 * and a notice must still clear the notch.
 */
const position: CSSProperties = {
  top: 'var(--snackbar-top, max(var(--edge-gap), env(safe-area-inset-top)))',
};

export function UndoToast() {
  const toasts = useToasts();
  const dismissToast = useUiStore((state) => state.dismissToast);

  if (toasts.length === 0) return null;

  return (
    <div
      style={position}
      className="pointer-events-none fixed inset-x-0 z-40 flex flex-col items-center gap-2 px-safe"
    >
      {toasts.slice(-VISIBLE_LIMIT).map((toast) => (
        <div key={toast.id} className="w-full max-w-sm">
          <Toast
            message={toast.message}
            actionLabel={toast.actionLabel}
            // UNDO asks the server for an inverse transfer; the queue entry carries
            // the transfer id it was created for. §12.2
            onAction={toast.onAction}
            onDismiss={() => {
              dismissToast(toast.id);
            }}
          />
        </div>
      ))}
    </div>
  );
}
